import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';

import {
  EMPTY_REPORT_HEADER,
  NoDataReason,
  WarningHeroReportName,
  WarningHeroTheme,
  WarningTrackingError,
} from './constants';
import {WarningHeroEl} from './index';

export function LoadingHero(params: {bodyText?: string; headerText?: string} = {}) {
  const {headerText, bodyText} = params || {};
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Loading,
        'header-text': headerText || ``,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function SensitiveDataHero({bodyText, reportName}: {bodyText: string; reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Sensitive,
        report: reportName,
        'header-text': `Report contains classified data`,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function UnsupportedActionHero({bodyText, reportName}: {bodyText: string; reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        'header-text': `Unsupported Request`,
        'body-text': bodyText,
        report: reportName,
        'tracking-error': WarningTrackingError.UnsupportedActionError,
      }}
    />
  );
}

export function MissingDataGroupMappingHero({
  property,
  reportName,
}: {
  property: string;
  reportName: WarningHeroReportName;
}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        'header-text': `Unable to Display Data`,
        'body-text': `The property "${property}" is no longer available for use. \n Please use another property or contact your project admin`,
        report: reportName,
        'tracking-error': WarningTrackingError.MissingMappedDatagroup,
      }}
    />
  );
}

export function ServerErrorHero({
  headerText,
  bodyText,
  reportName,
}: {
  headerText?: string;
  bodyText?: string;
  reportName: WarningHeroReportName;
}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        report: reportName,
        'header-text': headerText || `Oops, something seems to have gone wrong. Please try again.`,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function NoDataHero({
  bodyText,
  reportName,
  reason,
}: {
  bodyText?: string;
  reason?: NoDataReason;
  reportName: WarningHeroReportName;
}) {
  let headerText = `We couldn't find any data for your query.`;

  if (reason === NoDataReason.EmptyProject) {
    headerText = `We don't have any data for your project.`;
  } else if (reason === NoDataReason.Timeout) {
    headerText = `Sorry, the query timed out.`;
  }

  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.NoData,
        'header-text': headerText,
        report: reportName,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function EmptyDashboardAddReportHero({onAddReportClick}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': `Add a report to your board.`,
        report: WarningHeroReportName.Dashboards,
      }}
    >
      <ButtonEl
        attrs={{
          elref: `emptyAddReportButton`,
          icon: `plus`,
          theme: ButtonTheme.Primary,
        }}
        on={{click: onAddReportClick}}
      >
        Add Report
      </ButtonEl>
    </WarningHeroEl>
  );
}

export function EmptyBoardsAddReportHero() {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': `Create & save a report to your board.`,
        report: WarningHeroReportName.Dashboards,
      }}
    />
  );
}

export function EmptyDashboardCreateHero({onCreateDashboardClick}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': `Create a dashboard to monitor your metrics in one place.`,
        report: WarningHeroReportName.Dashboards,
      }}
    >
      <ButtonEl
        attrs={{
          elref: `emptyCreateDashboardButton`,
          icon: `plus`,
          theme: ButtonTheme.Primary,
        }}
        on={{click: onCreateDashboardClick}}
      >
        Create Dashboard
      </ButtonEl>
    </WarningHeroEl>
  );
}

export function EmptyLookupTableHero({onImportLookupTableClick}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': EMPTY_REPORT_HEADER[WarningHeroReportName.LookupTables],
        report: WarningHeroReportName.LookupTables,
      }}
    >
      <ButtonEl
        attrs={{
          icon: `plus`,
          theme: ButtonTheme.Primary,
        }}
        on={{click: onImportLookupTableClick}}
      >
        Import Lookup Table
      </ButtonEl>
    </WarningHeroEl>
  );
}

export function EmptyReportHero({reportName}: {reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': EMPTY_REPORT_HEADER[reportName],
        report: reportName,
      }}
    />
  );
}
